// Service for managing Tally prompts
import { fetchAuthSession } from 'aws-amplify/auth';

const getAuthHeaders = async () => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`
  } as const;
};

export interface TallyPrompt {
  promptId: string;
  name: string;
  description: string;
  createdBy?: string;
  toolGuidance?: string;
  openAIPromptId?: string;
  openAIVersion?: string;
  isLocal?: boolean;
  promptContent?: string;
  status: 'draft' | 'active' | 'archived';
  variables: Array<{
    name: string;
    description: string;
    defaultValue: string;
  }>;
  variableDefs?: Array<{
    name: string;
    label?: string;
    description?: string;
    type?: 'string' | 'enum' | 'number' | 'boolean';
    required?: boolean;
    allowedValues?: string[];
    defaultValue?: any;
    source?: 'user' | 'system' | 'skills-db' | 'context';
  }>;
  components?: Array<{
    id?: string;
    name?: string;
    description?: string;
    when?: Record<string, string[] | { in?: string[]; notIn?: string[] }>;
    priority?: number;
    content: string;
  }>;
  skillCategory?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export const tallyPromptService = {
  baseUrl(): string {
    return process.env.NODE_ENV === 'development' 
      ? '/Prod' 
      : (process.env.REACT_APP_API_BASE_URL || 'https://p811zb8knk.execute-api.us-east-1.amazonaws.com/Prod');
  },
  // Get the active main prompt
  async getActivePrompt(): Promise<TallyPrompt | null> {
    const baseUrl = this.baseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/tally-prompts?status=active`, { headers: await getAuthHeaders() });
      
      if (!response.ok) {
        console.error('Failed to fetch prompts:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      // Return the first active prompt (main Tally prompt)
      if (data.prompts && data.prompts.length > 0) {
        // Sort by createdAt to get the most recent active prompt
        const sorted = data.prompts.sort((a: TallyPrompt, b: TallyPrompt) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        return sorted[0];
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching active prompt:', error);
      return null;
    }
  },
  
  // Get prompt by skill category
  async getPromptBySkillCategory(category: string): Promise<TallyPrompt | null> {
    const baseUrl = this.baseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/tally-prompts?status=active`, { headers: await getAuthHeaders() });
      
      if (!response.ok) {
        console.error('Failed to fetch prompts:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      // Find prompt matching the skill category
      if (data.prompts) {
        const categoryPrompt = data.prompts.find((p: TallyPrompt) => 
          p.skillCategory === category && p.status === 'active'
        );
        
        if (categoryPrompt) {
          return categoryPrompt;
        }
      }
      
      // Fall back to main prompt
      return this.getActivePrompt();
    } catch (error) {
      console.error('Error fetching skill category prompt:', error);
      return null;
    }
  },
  
  // Get specific prompt by ID
  async getPromptById(promptId: string): Promise<TallyPrompt | null> {
    const baseUrl = this.baseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/tally-prompts/${promptId}`, { headers: await getAuthHeaders() });
      
      if (!response.ok) {
        console.error('Failed to fetch prompt:', response.status);
        return null;
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching prompt:', error);
      return null;
    }
  },
  
  // List prompts (optionally by status)
  async listPrompts(status?: 'draft' | 'active' | 'archived'): Promise<{ prompts: TallyPrompt[]; count: number }> {
    const baseUrl = this.baseUrl();
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetch(`${baseUrl}/secure/tally-prompts${qs}`, { headers: await getAuthHeaders() });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to list prompts: ${response.status} ${text}`);
    }
    return await response.json();
  },
  
  // Create a new prompt
  async createPrompt(payload: Partial<TallyPrompt> & { name: string; status?: 'draft' | 'active'; createdBy?: string }): Promise<TallyPrompt> {
    const baseUrl = this.baseUrl();
    const response = await fetch(`${baseUrl}/secure/tally-prompts`, {
      method: 'POST',
      headers: { ...(await getAuthHeaders()) },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to create prompt: ${response.status} ${text}`);
    }
    return await response.json();
  },
  
  // Update an existing prompt
  async updatePrompt(promptId: string, payload: Partial<TallyPrompt>): Promise<TallyPrompt> {
    const baseUrl = this.baseUrl();
    const response = await fetch(`${baseUrl}/secure/tally-prompts/${encodeURIComponent(promptId)}`, {
      method: 'PUT',
      headers: { ...(await getAuthHeaders()) },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to update prompt: ${response.status} ${text}`);
    }
    return await response.json();
  },
  
  // Archive (soft delete) a prompt
  async deletePrompt(promptId: string): Promise<{ message: string }> {
    const baseUrl = this.baseUrl();
    const response = await fetch(`${baseUrl}/secure/tally-prompts/${encodeURIComponent(promptId)}`, {
      method: 'DELETE',
      headers: { ...(await getAuthHeaders()) }
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to delete prompt: ${response.status} ${text}`);
    }
    return await response.json();
  }
};